import axios from 'axios';

const BACKEND_TARGET = (process.env.REACT_APP_BACKEND_TARGET || 'production').toLowerCase().trim();
const TARGET_SUFFIX = BACKEND_TARGET === 'test' ? 'TEST' : BACKEND_TARGET === 'dev' ? 'DEV' : 'PROD';
const DEFAULT_BACKEND_HOST = 'https://agent.freshroad.com';
const pickEnvByTarget = (baseName, fallback = '') => {
  const direct = process.env[baseName];
  if (direct) return direct;
  const targeted = process.env[`${baseName}_${TARGET_SUFFIX}`];
  return targeted || fallback;
};

// Use environment variable for API URL
// For local development, set REACT_APP_AUTH_API_URL=/api in .env to use proxy
// For production, set REACT_APP_AUTH_API_URL=https://agent.freshroad.com:3015/api
// Note: Auth endpoints (login, logout, register, etc.) use port 3015 (server.js)
const API_BASE_URL = pickEnvByTarget('REACT_APP_AUTH_API_URL', `${DEFAULT_BACKEND_HOST}:3015/api`);
// Agent/AI API (Sales, TTS, chatbot, etc.) – port 3016; empty = same-origin so proxy can forward
const AGENT_API_BASE_URL = pickEnvByTarget('REACT_APP_AGENT_API_URL', `${DEFAULT_BACKEND_HOST}:3016`);

// Create axios instance for auth/user (3015)
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Agent API instance (3016) – for kokoro-tts, chat, scripts, etc.; sends same auth token
const agentApi = axios.create({
  baseURL: AGENT_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});
agentApi.interceptors.request.use(
  (config) => {
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      if (config.headers?.['Content-Type']) {
        delete config.headers['Content-Type'];
      }
      if (config.headers?.common?.['Content-Type']) {
        delete config.headers.common['Content-Type'];
      }
    }

    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);
agentApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (!window.location.hash.includes('/login')) {
        window.location.href = '/#/login';
      }
    }
    return Promise.reject(error);
  }
);

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const requestUrl = config.url || '';
    const publicAuthEndpoints = [
      '/auth/login',
      '/auth/register',
      '/auth/verify-email',
      '/auth/verify-mfa',
      '/auth/resend-verification',
      '/auth/logout',
    ];
    const isPublicAuthEndpoint = publicAuthEndpoints.some((endpoint) => requestUrl.includes(endpoint));

    // Public auth endpoints should never include stale/invalid bearer tokens.
    if (isPublicAuthEndpoint) {
      if (config.headers?.Authorization) {
        delete config.headers.Authorization;
      }
      return config;
    }

    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const requestUrl = error.config?.url || '';

      // Don't redirect for public auth endpoints (login, register, verify, logout)
      // These endpoints may legitimately return 401 or don't require auth
      const publicAuthEndpoints = ['/auth/login', '/auth/register', '/auth/verify-email', '/auth/verify-mfa', '/auth/resend-verification', '/auth/logout'];
      const isPublicAuthEndpoint = publicAuthEndpoints.some(endpoint => requestUrl.includes(endpoint));

      if (!isPublicAuthEndpoint) {
        // Token expired or invalid - clear auth state and redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('user');

        // Redirect to login page (HashRouter uses hash-based routing)
        // Use full reload to ensure all state is cleared
        if (!window.location.hash.includes('/login')) {
          window.location.href = '/#/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  verifyEmail: (data) => api.post('/auth/verify-email', data),
  verifyMFA: (data) => api.post('/auth/verify-mfa', data),
  resendVerification: (data) => api.post('/auth/resend-verification', data),
  logout: () => api.post('/auth/logout'),
  getCurrentUser: () => api.get('/auth/me'),
  changePassword: (data) => api.post('/auth/change-password', data),
};

// User Management API
export const userAPI = {
  getAllUsers: () => api.get('/users'),
  updateUser: (userId, data) => api.put(`/users/${userId}`, data),
  deleteUser: (userId) => api.delete(`/users/${userId}`),
};

/** My Dashboard: call history + usage (Sales / agent API 3016; reads agentlogs) */
export const userDashboardAPI = {
  getCalls: (params) => agentApi.get('/api/user/calls', { params }),
  getUsage: (params) => agentApi.get('/api/user/usage', { params }),
};

// Groups API (invite, members, accept invite - for shared scripts/logs/analytics)
export const groupsAPI = {
  getMe: () => api.get('/groups/me'),
  invite: (email) => api.post('/groups/invite', { email }),
  listMembers: () => api.get('/groups/members'),
  listInvitations: () => api.get('/groups/invitations'),
  acceptInvite: (token) => api.post('/groups/accept-invite', { token }),
  cancelInvitation: (id) => api.delete(`/groups/invitations/${id}`),
};

export const crmV2API = {
  listGroups: (params = {}) => agentApi.get('/api/crm-v2/groups', { params }),
  getGroup: (groupId) => agentApi.get(`/api/crm-v2/groups/${groupId}`),
  createGroup: (data) => agentApi.post('/api/crm-v2/groups', data),
  deleteGroup: (groupId) => agentApi.delete(`/api/crm-v2/groups/${groupId}`),
  listRecords: (groupId, params = {}) => agentApi.get(`/api/crm-v2/groups/${groupId}/records`, { params }),
  bulkDeleteRecords: (recordIds) => agentApi.post('/api/crm-v2/records/bulk-delete', { recordIds }),
  updateRecord: (recordId, data) => agentApi.patch(`/api/crm-v2/records/${recordId}`, data),
  deleteRecord: (recordId) => agentApi.delete(`/api/crm-v2/records/${recordId}`),
  previewImport: (formData) => agentApi.post('/api/crm-v2/import/preview', formData),
  commitImport: (importSessionId) => agentApi.post('/api/crm-v2/import/commit', { importSessionId }),
};

// Agents API — an agent = settings + knowledge + a 1:1 scriptId reference (agent server 3016)
export const agentsAPI = {
  list: () => agentApi.get('/api/agents'),
  get: (id) => agentApi.get(`/api/agent/${id}`),
  save: (data) => agentApi.post('/api/save-agent', data),
  update: (data) => agentApi.post('/api/update-agent', data),
  remove: (_id, deleteScript = false) => agentApi.post('/api/delete-agent', { _id, deleteScript }),
};

// Agent templates — pre-built agent+script blueprints for "Start from a template" (agent server 3016)
export const templatesAPI = {
  list: () => agentApi.get('/api/agent-templates'),
  get: (id) => agentApi.get(`/api/agent-template/${id}`),
  save: (data) => agentApi.post('/api/save-agent-template', data),
};

export const analysisReportsAPI = {
  list: (scriptId) => agentApi.get('/api/analysis-reports', { params: { scriptId } }),
  get: (reportId) => agentApi.get(`/api/analysis-reports/${reportId}`),
  create: (data) => agentApi.post('/api/analysis-reports', data),
  delete: (reportId) => agentApi.delete(`/api/analysis-reports/${reportId}`),
};

// Billing API (Stripe integration)
export const billingAPI = {
  getBalance: () => api.get('/billing/balance'),
  getTransactions: (params) => api.get('/billing/transactions', { params }),
  createSetupIntent: () => api.post('/billing/setup-intent'),
  getPaymentMethods: () => api.get('/billing/payment-methods'),
  addPaymentMethod: (paymentMethodId) => api.post('/billing/payment-methods', { paymentMethodId }),
  removePaymentMethod: (id) => api.delete(`/billing/payment-methods/${id}`),
  setDefaultPaymentMethod: (id) => api.put(`/billing/payment-methods/${id}/default`),
  topUp: (amountCents, paymentMethodId) => api.post('/billing/top-up', { amountCents, paymentMethodId }),
  updateSettings: (settings) => api.put('/billing/settings', settings),
};

const PHONENUMBERS_PATH = '/api/phonenumbers';

// Force no cache so we always hit the server and get fresh data (avoid cache being used instead of MongoDB)
const NO_CACHE_HEADERS = {
  'Cache-Control': 'no-cache, no-store, must-revalidate',
  Pragma: 'no-cache',
  Expires: '0',
};

export const phonenumbersAPI = {
  list: () => agentApi.get(PHONENUMBERS_PATH, { headers: NO_CACHE_HEADERS }),
  get: (id) => agentApi.get(`${PHONENUMBERS_PATH}/${id}`, { headers: NO_CACHE_HEADERS }),
  getByNumber: (number) => agentApi.get(`${PHONENUMBERS_PATH}/by-number/${encodeURIComponent(number)}`, { headers: NO_CACHE_HEADERS }),
  create: (data) => agentApi.post(PHONENUMBERS_PATH, data, { headers: NO_CACHE_HEADERS }),
  update: (id, data) => {
    const path = `${PHONENUMBERS_PATH}/${id}`;
    const cacheBust = `_t=${Date.now()}`;
    const pathWithBust = `${path}?${cacheBust}`;
    const baseURL = agentApi.defaults.baseURL || '';
    const fullUrl = baseURL ? `${baseURL.replace(/\/$/, '')}${path}` : path;
    const payload = typeof data === 'object' && data !== null ? { ...data } : data;
    console.log('[phonenumbersAPI.update] PUT', fullUrl || `(same-origin)${path}`, '| id:', id, '| payload:', JSON.stringify(payload));
    return agentApi.put(pathWithBust, payload, { headers: NO_CACHE_HEADERS })
      .then((res) => {
        console.log('[phonenumbersAPI.update] response status:', res.status, '| data:', JSON.stringify(res.data));
        return res;
      })
      .catch((err) => {
        console.error('[phonenumbersAPI.update] error:', err.message, '| status:', err.response?.status, '| data:', err.response?.data);
        throw err;
      });
  },
  delete: (id) => agentApi.delete(`${PHONENUMBERS_PATH}/${id}`, { headers: NO_CACHE_HEADERS }),
};

export { agentApi };
export default api;
